import { Indicator } from "@mantine/core";
import { TriangleDownIcon } from "@modulz/radix-icons";
import { useState } from "react";
import styled, { useTheme } from "styled-components";
import { useRequestService } from "../hooks/useRequests";
import { LinkWithIcon } from "./LinkWithIcon";
import { SubNavigationList } from "./SubNavigationList";

type LinkProps = {
	text: string;
	icon: { default: string; active: string };
	to: string;
	subNavigationItems?: {
		text: string;
		to: string;
	}[];
};

type NavigationListProps = {
	className?: string;
	isHorizontal?: boolean;
	itemSpacing?: number;
	hideNavigation?: () => void;
	links: Array<LinkProps>;
};

const getMarginString = (isHorizontal?: boolean, spacing?: number): string => {
	const marginValue = spacing ? `${spacing}px` : 0;
	return isHorizontal ? `0 ${marginValue}` : `${marginValue} 0`;
};

const List = styled.ul<{ isHorizontal?: boolean; itemSpacing?: number }>`
  padding: 0;
  margin: 0;
  margin-top: 27px;
  list-style: none;
  background-color: #2e3844;
  padding-left: 16px;
  padding-top: 16px;
  padding-bottom: 16px;
  border-radius: 4px;

  ${(props) => `
        li {
            display: ${props.isHorizontal ? "inline" : "block"};
            margin: ${getMarginString(props.isHorizontal, props.itemSpacing)};
        }
    `}
`;

const NavHeader = styled.p`
  font-family: 'ProximaNovaBold', sans-serif;
  font-size: 14px;
  margin: 0;
`;

const NavHeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 16px;
  cursor: pointer;
`;
const NavigationList = ({
	className,
	links,
	isHorizontal,
	itemSpacing,
	hideNavigation,
}: NavigationListProps) => {
	const { useGetAccessRequestStats } = useRequestService();
	const [isNavVisible, setIsNavVisible] = useState(true);
	const { data: requestStats } = useGetAccessRequestStats();
	const numberOfPendingRequests = requestStats?.find(
		(request) => request.key === "PENDING",
	)?.value;

	const theme = useTheme();
	const { red } = theme.colors.secondary;

	return (
		<List
			className={className}
			isHorizontal={isHorizontal || false}
			itemSpacing={itemSpacing}
		>
			<NavHeaderSection onClick={() => setIsNavVisible(!isNavVisible)}>
				<NavHeader>Digital Cash</NavHeader>
				<TriangleDownIcon color="white" />
			</NavHeaderSection>
			{isNavVisible &&
				links.map((item, i) => {
					if (item.subNavigationItems) {
						return (
							<SubNavigationList
								link={item}
								key={i}
								hideNavigation={hideNavigation}
							/>
						);
					}
					if (item.to === "/requests" && numberOfPendingRequests) {
						//   Show pending requests indicator on request menu item
						return (
							<li key={`listitem-${i}`} className="m-[20px]">
								<Indicator
									inline
									label={numberOfPendingRequests}
									size={16}
									offset={0}
									color={red}
								>
									<LinkWithIcon
										to={item.to}
										text={item.text}
										icon={item.icon}
										onClick={hideNavigation}
									/>
								</Indicator>
							</li>
						);
					}
					return (
						<li key={`listitem-${i}`}>
							<LinkWithIcon
								to={item.to}
								text={item.text}
								icon={item.icon}
								onClick={hideNavigation}
							/>
						</li>
					);
				})}
		</List>
	);
};

export { NavigationList };
